import { Button, Modal, Select, Table } from 'antd';
import dayjs from 'dayjs';

export interface AnnexMergePreviewItem {
    contractNumber: number;
    description: string | null;
    quantity: number | string | null;
    unit: string | null;
}

interface AnnexMergeModalProps {
    open: boolean;
    dates: string[];
    selectedDates: string[];
    destinationDate: string | null;
    previewItems: AnnexMergePreviewItem[];
    previewLoading: boolean;
    onClose: () => void;
    onSelectedDatesChange: (dates: string[]) => void;
    onDestinationDateChange: (date: string) => void;
    onPreviewDate: (date: string) => void;
    onMerge: () => Promise<void>;
    t: (key: string) => string;
}

const AnnexMergeModal = ({
    open,
    dates,
    selectedDates,
    destinationDate,
    previewItems,
    previewLoading,
    onClose,
    onSelectedDatesChange,
    onDestinationDateChange,
    onPreviewDate,
    onMerge,
    t,
}: Readonly<AnnexMergeModalProps>) => (
    <Modal
        open={open}
        title={t('inventoryMovement.mergeInventories')}
        width={760}
        centered
        footer={null}
        onCancel={onClose}
    >
        <div
            className="annex-merge-controls"
            style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 12,
                marginBottom: 16,
            }}
        >
            <div style={{ flex: 1 }}>
                <label
                    className="annex-merge-destination"
                    htmlFor="annex-merge-destination"
                    style={{ display: 'block', marginBottom: 4 }}
                >
                    {t('inventoryMovement.newInventoryDate')}
                </label>
                <Select
                    id="annex-merge-destination"
                    aria-label={t('inventoryMovement.newInventoryDate')}
                    value={destinationDate ?? undefined}
                    options={selectedDates.map(date => ({
                        label: dayjs(date).format('YYYY-MM-DD'),
                        value: date,
                    }))}
                    disabled={selectedDates.length === 0}
                    onChange={onDestinationDateChange}
                    style={{ width: '100%' }}
                />
            </div>
            <Button
                type="primary"
                disabled={selectedDates.length === 0 || !destinationDate}
                onClick={() => void onMerge()}
            >
                {t('inventoryMovement.merge')}
            </Button>
        </div>
        <div
            className="annex-merge-scroll"
            style={{
                maxHeight: '55vh',
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                paddingRight: 4,
            }}
        >
            <Table
                aria-label={t('inventoryMovement.annexDates')}
                rowKey="date"
                dataSource={dates.map(date => ({ date }))}
                columns={[
                    {
                        title: t('inventoryMovement.inventoryDate'),
                        dataIndex: 'date',
                        render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
                    },
                ]}
                rowSelection={{
                    selectedRowKeys: selectedDates,
                    onChange: keys => onSelectedDatesChange(keys.map(String)),
                }}
                pagination={false}
                size="small"
                onRow={record => ({ onClick: () => onPreviewDate(record.date) })}
            />
            <Table
                aria-label={t('inventoryMovement.annexContents')}
                rowKey={(item, index) => `${item.contractNumber}-${item.description}-${index}`}
                dataSource={previewItems}
                loading={previewLoading}
                columns={[
                    { title: t('inventoryMovement.contractNumber'), dataIndex: 'contractNumber' },
                    { title: t('inventoryMovement.description'), dataIndex: 'description' },
                    {
                        title: t('inventoryMovement.quantity'),
                        dataIndex: 'quantity',
                        render: (quantity: number | string | null) =>
                            quantity === null ? '' : Number(quantity).toFixed(2),
                    },
                    { title: t('inventoryMovement.unit'), dataIndex: 'unit' },
                ]}
                pagination={false}
                size="small"
            />
        </div>
    </Modal>
);

export default AnnexMergeModal;
